import React, { useEffect, useRef } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import { useVoiceCall } from '../../../hooks/useVoiceCall';

const TestCallModal = ({ isOpen, toggle, agentId }) => {
  const scrollRef = useRef(null);

  const baseUrl = process.env.REACT_APP_AI_WSS_URL || 'ws://62.171.170.48:8085';
  const wsUrl = agentId ? `${baseUrl}/?agent_id=${agentId}` : null;

  const { isCallActive, isConnecting, error, startCall, endCall, audioData, messages } =
    useVoiceCall(wsUrl);

  useEffect(() => {
    if (!isOpen && isCallActive) {
      endCall();
    }
  }, [isOpen, isCallActive, endCall]);

  // Scroll to bottom of chat
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleToggleCall = () => {
    if (isCallActive) {
      endCall();
    } else {
      startCall();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" backdrop="static" keyboard={false}>
      <ModalHeader toggle={toggle} className="py-2">
        <div className="fs-5">AI Agent Test Call</div>
      </ModalHeader>

      <ModalBody className="p-0">
        <Row
          className="m-0"
          style={{
            minHeight: 'calc(100vh - 210px)'
          }}>
          <Col
            md="8"
            className="p-3 d-flex flex-column border-end bg-white"
            style={{
              maxHeight: 'calc(100vh - 210px)',
              overflowY: 'auto'
            }}>
            <div
              ref={scrollRef}
              className="flex-grow-1 overflow-auto pe-2"
              style={{ scrollBehavior: 'smooth' }}>
              {messages.length === 0 ? (
                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted opacity-50">
                  <Icon.MessageSquare size={48} className="mb-2" />
                  <p>No conversation yet</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`d-flex mb-3 ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                    <div
                      className={`d-flex ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'} align-items-end`}
                      style={{ maxWidth: '85%' }}>
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${msg.sender === 'user' ? 'ms-2 bg-primary' : 'me-2 bg-secondary'}`}
                        style={{ width: '28px', height: '28px' }}>
                        {msg.sender === 'user' ? (
                          <Icon.User size={14} className="text-white" />
                        ) : (
                          <Icon.Cpu size={14} className="text-white" />
                        )}
                      </div>
                      <div
                        className="p-2 rounded-3 shadow-sm px-3"
                        style={{
                          fontSize: '0.9rem',
                          backgroundColor: msg.sender === 'user' ? '#e3f2fd' : '#f5f5f5',
                          color: '#333',
                          borderBottomRightRadius: msg.sender === 'user' ? '2px' : '10px',
                          borderBottomLeftRadius: msg.sender === 'bot' ? '2px' : '10px'
                        }}>
                        <div>{msg.text}</div>
                        <div
                          className={`text-muted mt-1 ${msg.sender === 'user' ? 'text-end' : 'text-start'}`}
                          style={{ fontSize: '0.65rem', opacity: 0.7 }}>
                          {new Date(msg.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Col>

          {/* Controls & Waveform */}
          <Col
            md="4"
            className="p-4 d-flex flex-column align-items-center justify-content-center bg-light">
            {error && (
              <div className="alert alert-danger py-2 px-3 small w-100 mb-3 text-center">
                {error}
              </div>
            )}

            {/* Visualizer */}
            <div
              className="d-flex justify-content-center align-items-center mb-4 w-100"
              style={{ height: '60px', gap: '3px' }}>
              {audioData.map((val, i) => (
                <div
                  key={i}
                  className="bg-primary"
                  style={{
                    width: '4px',
                    height: `${Math.max(8, val * 100)}%`,
                    borderRadius: '2px',
                    transition: 'height 0.05s ease',
                    opacity: isCallActive ? 0.8 : 0.2
                  }}
                />
              ))}
            </div>

            <div className="mb-4">
              <div className="position-relative">
                {isCallActive && (
                  <div
                    className="position-absolute translate-middle start-50 top-50 w-100 h-100 rounded-circle bg-success opacity-25 animate-ping"
                    style={{ pointerEvents: 'none' }}></div>
                )}
                <Button
                  color={isCallActive ? 'danger' : 'success'}
                  className="rounded-circle p-4 shadow-lg border-0 d-flex align-items-center justify-content-center"
                  onClick={handleToggleCall}
                  disabled={isConnecting}
                  style={{ width: '80px', height: '80px' }}>
                  {isConnecting ? (
                    <Icon.Loader className="spin" size={32} />
                  ) : isCallActive ? (
                    <Icon.PhoneOff size={32} />
                  ) : (
                    <Icon.Phone size={32} />
                  )}
                </Button>
              </div>
            </div>

            <div className={`fw-bold mb-1 ${isCallActive ? 'text-success' : 'text-muted'}`}>
              {isConnecting ? 'Connecting...' : isCallActive ? 'CALL ACTIVE' : 'READY TO START'}
            </div>
            {isCallActive && <div className="small text-muted animate-pulse">Speak now...</div>}
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="bg-light py-2">
        <Button color="secondary" onClick={toggle} className="px-4 btn-sm">
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

TestCallModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  agentId: PropTypes.string
};

export default TestCallModal;
